


import { Component, Vue } from "vue-property-decorator";

import { CustomNotification } from "@Internal/Models";

import { ApiKeys } from "App";
import { Filter, Identifiable } from "App/Contracts";
import { $t, EventBus } from "App/IOC";
import { Column, PagerSorter } from "App/Models";
import { ApiKey } from "App/Models/ApiKey";

import { TableAction } from "Web/Contracts";

import RecordsComponent from "Web/Components/Records.vue";

import GenerateForm from "./GenerateForm.vue";
import RevealWindow from "./Reveal.vue";

/* CLES API ( MCP ) DES CLIENTS — 06/10/2026.

   Le client ne cree ni ne revoque de clef : c'est nous, ici ( brief back
   `evol/api-key-auth` ). Il les voit dans son espace ( Parametrage → Cles MCP et
   API ) et peut reafficher une clef active.

   Une liste, toutes clefs confondues, plus recentes d'abord ( l'ordre de l'API ) ;
   « Generer une clé » en tete ; par ligne, « Afficher la clé » et « Révoquer ». */
@Component({
    components: {
        GenerateForm,
        RevealWindow,
        Records: RecordsComponent
    }
})
export default class ApiKeysView extends Vue {
    $refs!: Vue[ "$refs" ] & {
        Records: RecordsComponent,
    };

    private records: ApiKey[] = [];

    private columns: Column[] = [
        Column.Static( "UserEmail", $t( "API_KEYS_columnUser" ) ),
        Column.Static( "Name", $t( "API_KEYS_columnName" ) ),
        Column.Static( "Prefix", $t( "API_KEYS_columnPrefix" ) ),
        Column.Static( "Status", $t( "API_KEYS_columnStatus" ) ),
        Column.Static( "CreatedAt", $t( "API_KEYS_columnCreated" ) ),
        Column.Static( "LastUsedAt", $t( "API_KEYS_columnLastUsed" ) ),
        Column.Static( "ExpiresAt", $t( "API_KEYS_columnExpires" ) )
    ];

    private pagerSorter: PagerSorter = new PagerSorter( 20 );

    private actions: TableAction<ApiKey>[] = [
        {
            Id: "reveal",
            ShortName: "visibility",
            Name: $t( "API_KEYS_actionReveal" ),
            Multi: false
        },
        {
            Id: "revoke",
            ShortName: "block",
            Name: $t( "API_KEYS_actionRevoke" ),
            Multi: false
        }
    ];

    private showForm: boolean = false;

    /* L'ECHEC SE DIT : « aucune clef » et « la liste n'a pas pu se lire » ne
       veulent pas dire la meme chose ( 06/10/2026 : un 404 du back s'affichait
       en « Aucune clé API » ). */
    private failed: boolean = false;

    /* La clef dont on affiche le jeton ; `null` = fenetre fermee. */
    private revealed: ApiKey | null = null;

    /* « Afficher » seulement sur une clef active dont l'API connait le jeton
       ( un prefixe = creee apres le chiffrement ) ; « Revoquer » sur toute clef
       qui ne l'est pas deja — une clef expiree se revoque aussi. */
    private isActionAllowed( row: Identifiable, action: Identifiable ): boolean {
        const key = row as ApiKey;

        if( action.Id === "reveal" )
            return key.Status === "active" && key.Prefix !== "";

        if( action.Id === "revoke" )
            return key.Status !== "revoked";

        return true;
    }

    private async update(
        _filters: Filter[],
        pagerSorter: PagerSorter,
        resetPagination?: boolean
    ): Promise<void> {
        await this.$nextTick();
        this.$refs.Records.Loading();

        if( resetPagination )
            pagerSorter.Page = 1;

        const [ result, error ] = await ApiKeys.List( pagerSorter.Page, pagerSorter.Limit );

        const updated = pagerSorter.Copy();
        updated.Total = ( result !== undefined ) ? result.Total : 0;

        this.pagerSorter = updated;
        this.failed = ( error !== undefined );
        this.records = ( result !== undefined ) ? result.Keys : [];

        if( error !== undefined )
            EventBus.Publish( "Error", error );
    }

    private refresh(): void {
        this.update( [], this.pagerSorter, false );
    }

    private executeAction( id: string, rows: ApiKey[] ): void {
        if( rows.length === 0 )
            return;

        const key = rows[ 0 ];

        switch( id ) {
            case "reveal":
                this.revealed = key;
                break;

            case "revoke":
                EventBus.Publish( "Confirmation", {
                    Props: {
                        Message: this.$t( "API_KEYS_revokeConfirm", {
                            name: key.Name,
                            email: key.UserEmail
                        } ),
                        Actions: [{
                            Id: "revoke",
                            Name: this.$t( "API_KEYS_actionRevoke" ),
                            Active: true
                        }]
                    },
                    Events: { Action: () => this.revoke( key ) }
                });

                break;
        }
    }

    private async revoke( key: ApiKey ): Promise<void> {
        const error = await ApiKeys.Revoke( key.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "API_KEYS_title" ), $t( "API_KEYS_revoked" )
        ));

        this.refresh();
    }

    private onGenerated(): void {
        this.refresh();
    }

    private statusLabel( key: ApiKey ): string {
        return this.$t( `API_KEYS_status_${key.Status}` ) as string;
    }

    /* L'API de la Console rend ses dates en UTC, sans fuseau
       ( `YYYY-MM-DD HH:MM:SS` ) : on les relit comme telles avant de les
       afficher a l'heure du poste. */
    private formatDate( value: string, withTime: boolean ): string {
        if( value === "" )
            return "";

        const date = new Date( value.replace( " ", "T" ) + "Z" );

        if( isNaN( date.getTime() ) )
            return value;

        return withTime
            ? date.toLocaleString( "fr-FR", { dateStyle: "short", timeStyle: "short" } as any )
            : date.toLocaleDateString( "fr-FR" );
    }
}
